"use client";

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, X } from "lucide-react";
import { useParams } from "next/navigation";
import { toast } from "sonner";
import { z } from "zod";

import { FormTextarea } from "@/components/custom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form, FormField } from "@/components/ui/form";
import { useAuth } from "@/context";
import { useFetch } from "@/hooks/useFetch";
import { AlbumType, LectureType } from "@/lib/schema";

import { createFeedback } from "./actions";

const MAX_DESCRIPTION_LENGTH = 1200;

const formSchema = z
  .object({
    text: z
      .string()
      .min(5, "Үндсэн хэсэг доод тал нь 5н тэмдэгт байх хэрэгтэй")
      .max(
        MAX_DESCRIPTION_LENGTH,
        `Үндсэн хэсэг дээд тал нь ${MAX_DESCRIPTION_LENGTH} тэмдэгт байх боломжтой`
      ),
  })
  .required();

export function FeedbackLecture() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [descriptionLength, setDescriptionLength] = useState(0);
  const { isAuth } = useAuth();
  const params = useParams();
  const [fetchLecture, { loading: loadingLecture, data: lectureData }] =
    useFetch<{
      data: LectureType;
    }>();
  const [fetchAlbum, { loading: loadingAlbum, data: albumData }] = useFetch<{
    data: AlbumType;
  }>();

  useEffect(() => {
    if (isAuth) {
      fetchLecture(`/lecture/${params.track}`, {
        cache: "no-store",
        memoryCache: true,
      });
      fetchAlbum(`/album/${params.id}`, {
        cache: "no-store",
        memoryCache: true,
      });
    }
  }, [isAuth]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      text: "",
    },
  });

  const closeDialog = (o: boolean) => {
    form.reset();
    setOpen(o);
    setLoading(false);
  };

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    setLoading(true);
    createFeedback({
      ...values,
      albumId: String(params.id),
      lectureId: String(params.track),
      productId: String(lectureData?.data.product_id),
    } as any)
      .then(() => toast.success("Амжилттай нийтлэгдлээ"))
      .catch((c) => toast.error(c.message))
      .finally(() => closeDialog(false));
  };

  const isPaid = albumData?.data.album.isPaid || lectureData?.data.isPaid;

  if (loadingLecture || loadingAlbum || !isPaid) return null;
  return (
    <Dialog onOpenChange={closeDialog} open={open}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="bg-[#FBF9F8] py-3 px-4 gap-3 rounded-lg text-sm flex items-center justify-between leading-none w-full mb-8"
          onClick={() => setOpen(true)}
        >
          Сэтгэгдэл бичих
          <ArrowRight size={20} />
        </button>
      </DialogTrigger>
      <DialogContent
        className="w-11/12 sm:max-w-[530px] rounded-xl sm:rounded-3xl"
        hideClose
      >
        <DialogHeader className="space-y-0 flex-row items-center justify-between">
          <DialogTitle className="text-2xl">Та сэтгэгдлээ бичнэ үү</DialogTitle>
          <DialogClose className="h-6 w-6 items-center justify-center flex">
            <X color="#21232C" size={18} />
          </DialogClose>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-8 mt-8"
          >
            <FormField
              control={form.control}
              name="text"
              render={({ field }) => (
                <FormTextarea
                  placeholder="Сэтгэгдэл бичих"
                  className="py-4"
                  field={field}
                  maxLength={MAX_DESCRIPTION_LENGTH}
                  onChange={(e) => {
                    field.onChange(e);
                    setDescriptionLength(e.target.value.length);
                  }}
                  nodeAfterError={
                    <div className="text-right text-sm text-muted-foreground">
                      {descriptionLength}/{MAX_DESCRIPTION_LENGTH}
                    </div>
                  }
                />
              )}
            />
            <Button
              className="rounded-xl px-6 w-full"
              type="submit"
              disabled={loading}
            >
              Сэтгэгдэл үлдээх
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
